/**
 * ================================================================
 * CUSTOM HOOK: useClickOutside
 * ================================================================
 *
 * @hook             useClickOutside
 * @description      Hook to detect clicks outside of a referenced element
 * @category         Utility Hook
 *
 * @parameters
 * - ref: RefObject<HTMLElement> - Reference to the element to detect outside clicks
 * - callback: () => void - Function to call when clicking outside
 * - enabled?: boolean - Whether the hook is active (default: true)
 *
 * @usage
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * useClickOutside(ref, () => setIsOpen(false));
 * ```
 * ================================================================
 */

import { RefObject, useEffect } from 'react';

export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T>,
  callback: () => void,
  enabled: boolean = true,
) {
  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback, enabled]);
}
